// src/admin/pages/RunDetailPage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ExternalLink, AlertTriangle, AlertCircle, Copy, Check, Download } from 'lucide-react';
import { getRunDetail } from '../runsApi.js';
import { useAuth } from '../../auth/AuthContext.jsx';
import StatusBadge from '../components/StatusBadge.jsx';
import JsonViewer from '../components/JsonViewer.jsx';
import Disclosure from '../components/Disclosure.jsx';
import BriefPreview from '../../components/ui/BriefPreview.jsx';
import renderResultEntry from '../../components/outputs/renderResultEntry.jsx';
import { briefEnvelopeToFormState } from '../../utils/briefEnvelopeToFormState.js';
import { getModeConfig, ESC_ACCENT } from '../../constants/modes.js';

const STATUS_LABEL_IT = {
  completed: 'completata', approved: 'approvata',
  failed: 'fallita', in_progress: 'in corso', pending: 'in attesa',
  cancelled: 'annullata', canceled: 'annullata',
};
const EMPTY_RESULT_EXPLANATION = {
  failed:     'Questa run è fallita prima di produrre contenuto. Vedi l\'errore qui sopra per i dettagli.',
  in_progress:'Generazione in corso. Aggiorna la pagina tra qualche istante per vedere il risultato.',
  pending:    'Run in attesa di esecuzione.',
  cancelled:  'Run annullata prima del completamento.',
  canceled:   'Run annullata prima del completamento.',
};

// Light theme tokens — match every key the per-platform renderers consume
// (mirror of EscContentCreatorPro.jsx:464-467 light branch with renderer-required extras).
const LIGHT_T = {
  bg:          '#ffffff',
  surface:     'rgba(0,0,0,0.015)',
  inputBg:     '#ffffff',
  inputBorder: 'rgba(0,0,0,0.18)',
  border:      'rgba(0,0,0,0.10)',
  text:        '#1d1d1f',
  textSoft:    '#1d1d1f',
  textMuted:   '#3a3a3c',
  textFaint:   '#6c6c6d',
  textDim:     '#6c6c6d',
  btnText:     '#ffffff',
};
// Opacity helper used by renderers as `background: w(0.03)` etc.
// MUST be a function; replicates the light-mode branch of EscContentCreatorPro's `w`.
const LIGHT_W = (opacity) =>
  `rgba(0,0,0,${Math.min(opacity * 2.5, 0.2).toFixed(2)})`;

const cardStyle = {
  background: '#ffffff', borderRadius: '10px',
  border: '1px solid rgba(0,0,0,0.06)', padding: '20px',
  marginBottom: '20px',
};
const btnStyle = {
  display: 'inline-flex', alignItems: 'center', gap: '6px',
  padding: '6px 12px', borderRadius: '6px',
  border: '1px solid rgba(0,0,0,0.1)', background: '#ffffff',
  color: '#3a3a3c', fontSize: '12px', cursor: 'pointer',
  fontFamily: "'DM Sans', sans-serif", textDecoration: 'none',
};
const primaryBtnStyle = {
  ...btnStyle,
  background: ESC_ACCENT, color: '#fff', border: 'none', fontWeight: 600,
};

function Meta({ label, value }) {
  return (
    <div style={{ minWidth: '160px' }}>
      <div style={{ fontSize: '11px', color: '#6c6c6d', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      <div style={{ fontSize: '13px', color: '#1d1d1f', marginTop: '2px', wordBreak: 'break-word' }}>{value || '—'}</div>
    </div>
  );
}

export default function RunDetailPage() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSuperadmin } = useAuth();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [copiedError, setCopiedError] = useState(false);
  // Drives the "Copia/Copiato" UI inside per-platform renderers. Without
  // real handlers the copy buttons inside the rendered Result silently no-op
  // and look broken to the user.
  const [copiedId, setCopiedId] = useState(null);
  const onCopy = async (id, text) => {
    try {
      await navigator.clipboard.writeText(text == null ? '' : String(text));
      setCopiedId(id);
      setTimeout(() => setCopiedId(curr => (curr === id ? null : curr)), 1500);
    } catch { /* clipboard unavailable */ }
  };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    getRunDetail(taskId)
      .then(d => { if (alive) setDetail(d); })
      .catch(err => { if (alive) setError(err.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [taskId]);

  async function handleCopyErrorDetails() {
    if (!detail) return;
    const details = {
      task_id: detail.task_id, status: detail.status,
      error_code: detail.error_code, error_message: detail.error_message,
      created_at: detail.created_at, completed_at: detail.completed_at,
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(details, null, 2));
      setCopiedError(true);
      setTimeout(() => setCopiedError(false), 1500);
    } catch { /* clipboard unavailable */ }
  }

  // Build synthetic history entry for both: the in-page Result renderer AND
  // the cross-route reload payload. Matches the shape produced by
  // useGeneration.executeGeneration (lines 215-245 of src/hooks/useGeneration.js).
  const synthEntry = useMemo(() => {
    if (!detail || !detail.result_json) return null;
    const result = detail.result_json;
    // Derive activeMode from the BE-saved brief (mirrors briefEnvelopeToFormState).
    const fe = detail.brief_json ? briefEnvelopeToFormState(detail.brief_json) : {};
    const activeMode = fe.activeMode || detail.platform;
    return {
      id: detail.task_id,
      timestamp: detail.completed_at ? new Date(detail.completed_at) : new Date(detail.created_at),
      mode: activeMode,
      modeConfig: getModeConfig(activeMode),
      results: {
        _taskId: detail.task_id,
        content: result.content,
        contentFormat: result.content_format ?? detail.content_format ?? null,
        platform: result.platform ?? detail.platform,
        topic: result.topic ?? detail.topic,
        factualConfidence: detail.critic_score,
        styleScore: detail.style_score,
        status: detail.status,
        research: result.research || null,
        reviews:  result.reviews  || null,
        drafts:   result.drafts   || null,
        style:    result.style    || null,
        metadata: result.metadata || null,
        sources:  result.sources  || null,
        warnings: result.warnings ?? null,
        warningsCount: detail.warnings_count ?? null,
      },
      params: detail.brief_json
        ? { ...detail.brief_json, activeMode, socialChannel: fe.socialChannel, emailChannel: fe.emailChannel }
        : null,
      generationDuration: null,
    };
  }, [detail]);

  const langfuseUrl = isSuperadmin && detail?.langfuse_url ? detail.langfuse_url : null;
  // Derive the list path by dropping the trailing /:taskId segment so the page
  // works under every mount: /admin/runs, /tenant-admin/runs, and the
  // member-facing /runs. location.state.from still overrides when present.
  const defaultBack = location.pathname.replace(/\/[^/]+$/, '') || '/runs';
  const backTo = (location.state && location.state.from) || defaultBack;

  function handleBack() { navigate(backTo); }

  function handleReloadIntoGenerator() {
    if (!detail?.brief_json) return;
    navigate('/content-generator', {
      state: {
        preloadedBrief: detail.brief_json,
        preloadedHistoryEntry: synthEntry,
      },
    });
  }

  if (loading) return <div style={{ padding: '40px', color: '#6c6c6d', fontFamily: "'DM Sans', sans-serif" }}>Caricamento...</div>;
  if (error)   return <div style={{ padding: '40px', color: '#EF4444', fontFamily: "'DM Sans', sans-serif" }}>{error}</div>;
  if (!detail) return <div style={{ padding: '40px', color: '#6c6c6d', fontFamily: "'DM Sans', sans-serif" }}>Run non trovata.</div>;

  const result = detail.result_json;
  const resultAvailable = !!result && (detail.status === 'completed' || detail.status === 'approved');
  const warnings = result && Array.isArray(result.warnings) ? result.warnings : [];
  const showReloadBtn = !isSuperadmin && !!detail.brief_json;

  return (
    <div style={{ padding: '32px 40px', fontFamily: "'DM Sans', sans-serif", color: '#1d1d1f', maxWidth: '1100px' }}>
      <button onClick={handleBack} style={{ ...btnStyle, marginBottom: '20px', background: 'transparent', border: 'none' }}>
        <ArrowLeft size={15} /> Torna alla lista
      </button>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', gap: '16px' }}>
        <div>
          <h1 style={{ fontSize: '20px', fontWeight: 700, margin: 0, wordBreak: 'break-word' }}>
            {detail.topic || '— senza titolo —'}
          </h1>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '6px' }}>
            <StatusBadge status={detail.status} />
            <code style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: '#6c6c6d' }}>
              {detail.task_id}
            </code>
          </div>
        </div>
        {isSuperadmin && (
          langfuseUrl ? (
            <a href={langfuseUrl} target="_blank" rel="noopener noreferrer" style={btnStyle}>
              <ExternalLink size={13} /> Langfuse trace
            </a>
          ) : (
            <span style={{ ...btnStyle, opacity: 0.5, cursor: 'not-allowed' }} title="Link non disponibile" aria-disabled="true">
              <ExternalLink size={13} /> Langfuse trace
            </span>
          )
        )}
      </div>

      {/* Meta card */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
          <Meta label="Tenant"      value={detail.tenant_id} />
          <Meta label="Platform"    value={`${detail.platform || '—'}${detail.content_format ? ' / ' + detail.content_format : ''}`} />
          <Meta label="Objective"   value={detail.objective} />
          <Meta label="Persona"     value={detail.persona} />
          <Meta label="Created"     value={detail.created_at   ? new Date(detail.created_at).toLocaleString('it-IT') : null} />
          <Meta label="Completed"   value={detail.completed_at ? new Date(detail.completed_at).toLocaleString('it-IT') : null} />
          <Meta label="Reviewed"    value={detail.reviewed_at  ? new Date(detail.reviewed_at).toLocaleString('it-IT') : null} />
          <Meta label="Iterations"  value={detail.iteration_count != null ? String(detail.iteration_count) : null} />
          <Meta label="Critic score" value={detail.critic_score != null ? String(detail.critic_score) : null} />
          <Meta label="Style score"  value={detail.style_score  != null ? String(detail.style_score)  : null} />
          <Meta label="Fact-check"   value={detail.fact_check_score != null ? String(detail.fact_check_score) : null} />
          <Meta label="Created by"   value={detail.created_by_email} />
          <Meta label="Reviewed by"  value={detail.reviewed_by_email} />
        </div>
      </div>

      {/* Error card */}
      {detail.status === 'failed' && (detail.error_message) && (
        <div style={{ ...cardStyle, background: 'rgba(239,68,68,0.04)', border: '1px solid rgba(239,68,68,0.2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#EF4444', fontWeight: 600, fontSize: '13px' }}>
              <AlertCircle size={15} /> Errore
            </div>
            <button
              type="button"
              onClick={handleCopyErrorDetails}
              title="Copia i dettagli da inviare al supporto"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                padding: '4px 8px', borderRadius: '6px',
                border: '1px solid rgba(0,0,0,0.1)', background: '#fff',
                color: copiedError ? '#10B981' : '#3a3a3c',
                fontSize: '11px', cursor: 'pointer',
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              {copiedError ? <Check size={12} /> : <Copy size={12} />}
              {copiedError ? 'Copiato' : 'Copia dettagli errore'}
            </button>
          </div>
          <div style={{ fontSize: '13px', color: '#1d1d1f' }}>{detail.error_message}</div>
          {detail.error_code && (
            <div style={{ fontSize: '11px', color: '#6c6c6d', marginTop: '6px', fontFamily: "'JetBrains Mono', monospace" }}>
              {detail.error_code}
            </div>
          )}
        </div>
      )}

      {/* Warnings card */}
      {(detail.warnings_count > 0 || warnings.length > 0) && (
        <div style={{ ...cardStyle, background: 'rgba(245,158,11,0.04)', border: '1px solid rgba(245,158,11,0.25)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#B45309', fontWeight: 600, fontSize: '13px', marginBottom: '8px' }}>
            <AlertTriangle size={15} /> {detail.warnings_count || warnings.length} warning
          </div>
          {warnings.map((w, i) => (
            <div key={i} style={{ fontSize: '13px', color: '#1d1d1f', marginTop: i === 0 ? 0 : '6px' }}>
              <strong>{w.agent}:</strong> {w.message}
            </div>
          ))}
        </div>
      )}

      {/* Input parameters */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, margin: 0 }}>Parametri di input</h3>
          {showReloadBtn && (
            <button
              type="button"
              onClick={handleReloadIntoGenerator}
              style={primaryBtnStyle}
              title="Carica gli input e l'output di questa run nel Content Generator"
            >
              <Download size={13} /> Ricarica nel Content Generator
            </button>
          )}
        </div>
        {detail.brief_json
          ? <BriefPreview brief={detail.brief_json} t={LIGHT_T} color={ESC_ACCENT} />
          : <div style={{ padding: '16px', color: '#6c6c6d', fontSize: '13px', fontStyle: 'italic' }}>
              Input non disponibile per questa run.
            </div>}
      </div>

      {/* Result */}
      <div style={cardStyle}>
        <h3 style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 10px' }}>Result (output)</h3>
        {resultAvailable && synthEntry
          ? renderResultEntry(synthEntry, {
              t: LIGHT_T,
              w: LIGHT_W,                       // callable — see Design > Light theme constants
              copiedId,                         // local state — wires copy buttons inside renderers
              onCopy,                           // local handler — see useState above
              onGetHtml: () => '',              // BlogResultEntry reads this; stub is safe (no "export HTML" affordance needed here)
            })
          : <div style={{ padding: '16px', color: '#6c6c6d', fontSize: '13px', lineHeight: 1.5 }}>
              <div style={{ fontStyle: 'italic' }}>
                Non disponibile (stato: {STATUS_LABEL_IT[detail.status] || detail.status})
              </div>
              {EMPTY_RESULT_EXPLANATION[detail.status] && (
                <div style={{ marginTop: '6px' }}>{EMPTY_RESULT_EXPLANATION[detail.status]}</div>
              )}
            </div>}
      </div>

      {/* Intermediate outputs — collapsible */}
      {result && result.drafts && (
        <div style={cardStyle}>
          <Disclosure title="Outputs Writer (drafts)" defaultOpen={false}>
            <JsonViewer value={result.drafts} />
          </Disclosure>
        </div>
      )}
      {result && result.reviews && (
        <div style={cardStyle}>
          <Disclosure title="Outputs Reviews" defaultOpen={false}>
            <JsonViewer value={result.reviews} />
          </Disclosure>
        </div>
      )}
      {result && result.style && (
        <div style={cardStyle}>
          <Disclosure title="Outputs Style" defaultOpen={false}>
            <JsonViewer value={result.style} />
          </Disclosure>
        </div>
      )}
      {result && result.research && (
        <div style={cardStyle}>
          <Disclosure title="Research" defaultOpen={false}>
            <JsonViewer value={result.research} />
          </Disclosure>
        </div>
      )}
      {result && result.sources && (
        <div style={cardStyle}>
          <Disclosure title="Sources" defaultOpen={false}>
            <JsonViewer value={result.sources} />
          </Disclosure>
        </div>
      )}
    </div>
  );
}
